import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Eyebrow, PageHead } from '../ui/index.js';
import { useGames } from '../api/hooks.js';
import { useInstalledGames } from '../components/game/useInstallState.js';
import { FocusZone } from '../components/focus/Focusable.js';
import { FocusableTextField } from '../components/focus/OnScreenKeyboard.js';
import { GameCard } from '../components/game/GameCard.js';
import { EmptyState } from '../components/layout/Loading.js';
// Searching is the library's own matching, so a word that finds a game on one screen finds it
// on the other. There was a second copy of it here, and the two had already drifted: this one
// never looked at a genre.
import { matchesQuery, normalise } from '../library/view.js';

export const Route = createFileRoute('/search')({
  component: Search,
  validateSearch: (search: Record<string, unknown>): { q?: string } =>
    typeof search.q === 'string' && search.q.length > 0 ? { q: search.q.slice(0, 80) } : {},
});

/**
 * Search, which the brand board puts in the rail beside Home.
 *
 * Everything it looks through is already in the cache — the catalog is loaded for the shelves
 * — so this filters rather than asks the API, and it answers on every keystroke without a
 * spinner.
 *
 * The field is the one from `OnScreenKeyboard`, so a controller can type here too. Without
 * that, Search would be a destination a gamepad could reach and not use.
 */
function Search() {
  const navigate = useNavigate();
  const { q = '' } = Route.useSearch();
  const [query, setQuery] = React.useState(q);

  const games = useGames();
  const { games: installed } = useInstalledGames();

  // The address follows the field, a beat behind, so a result is a link someone can send.
  React.useEffect(() => {
    const timer = setTimeout(() => {
      void navigate({
        to: '/search',
        search: query ? { q: query } : {},
        replace: true,
      });
    }, 350);
    return () => clearTimeout(timer);
  }, [query, navigate]);

  const needle = normalise(query);
  const installedIds = new Set(installed.map((g) => g.gameId));

  const gameHits = needle ? (games.data ?? []).filter((game) => matchesQuery(game, needle)) : [];

  const total = gameHits.length;

  return (
    <FocusZone focusKey="search" direction="column" className="gb-stack gb-stack--loose">
      <PageHead
        eyebrow="Find it"
        title="Search."
        description="Type a title, a studio, or one of us."
      />

      <div style={{ maxWidth: '34rem' }}>
        <FocusableTextField
          autoFocus
          value={query}
          onChange={setQuery}
          label="What are you looking for?"
          placeholder="Lone Mechanic, level design, Tuesday…"
          maxLength={80}
        />
      </div>

      {needle.length === 0 ? (
        <EmptyState
          title="Everything we have made is in here."
          body="Start typing and the shelves narrow as you go."
        />
      ) : total === 0 ? (
        <EmptyState
          title={`Nothing matches “${query}”.`}
          body="Try a shorter word, or browse the catalog instead."
        />
      ) : (
        <>
          <Eyebrow tone="quiet" small>
            {`${total} ${total === 1 ? 'result' : 'results'}`}
          </Eyebrow>

          {gameHits.length > 0 ? (
            <section className="gb-section">
              <Eyebrow>{'Games'}</Eyebrow>
              <FocusZone focusKey="search-games" className="gb-grid gb-grid--capsule">
                {gameHits.map((game, index) => (
                  <GameCard
                    key={game.id}
                    game={game}
                    autoFocus={index === 0}
                    installed={installedIds.has(game.id)}
                  />
                ))}
              </FocusZone>
            </section>
          ) : null}
        </>
      )}
    </FocusZone>
  );
}
